import { AppState, IStateManager, DisplayMode, GestureEvent, PersonCollection, PersonID, VisualizationType } from '../../types';
import { getCurrentColor } from '../animation-engine/utils/ColorUtils';
import { GestureActionMapper } from './GestureActionMapper';
import { logger } from '../../utils/logger';

/**
 * StateManager - Central state store for the application
 * Validates Requirements 3.4.1, 3.4.2, 3.4.3, 3.4.4, 10.1, 10.2
 */
export class StateManager implements IStateManager {
  private state: AppState;
  private subscribers: Array<(state: AppState) => void> = [];

  constructor() {
    // Initialize with default state
    const initialColor = getCurrentColor();
    this.state = {
      displayMode: DisplayMode.ARTISTIC,
      visualConfig: {
        primaryColor: initialColor,
        secondaryColor: initialColor,
        trailLength: 30,
        trailOpacity: 0.8,
        stickFigureThickness: 3,
      },
      debugOverlayEnabled: false, // Default: debug overlay disabled
      people: {
        people: new Map(),
        frameCount: 0
      }
    };
  }

  /**
   * Get current application state
   * @returns Current state (immutable copy)
   */
  getState(): AppState {
    // Return a copy to prevent external mutations
    return {
      ...this.state,
      visualConfig: { ...this.state.visualConfig },
      people: {
        people: new Map(this.state.people.people),
        frameCount: this.state.people.frameCount
      }
    };
  }

  /**
   * Update application state with partial updates
   * @param partial Partial state to merge with current state
   */
  setState(partial: Partial<AppState>): void {
    // Create new state object (immutability)
    const oldState = this.state;
    
    this.state = {
      ...oldState,
      ...partial,
      // Deep copy nested objects if they're provided
      visualConfig: partial.visualConfig
        ? { ...oldState.visualConfig, ...partial.visualConfig }
        : { ...oldState.visualConfig },
      people: partial.people
        ? {
            people: new Map(partial.people.people),
            frameCount: partial.people.frameCount
          }
        : {
            people: new Map(oldState.people.people),
            frameCount: oldState.people.frameCount
          }
    };

    // Notify all subscribers
    this.notifySubscribers();
  }

  /**
   * Subscribe to state changes
   * @param callback Function to call when state changes
   * @returns Unsubscribe function
   */
  subscribe(callback: (state: AppState) => void): () => void {
    this.subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Toggle between display modes
   */
  toggleDisplayMode(): void {
    const newMode = this.state.displayMode === DisplayMode.ARTISTIC
      ? DisplayMode.POSE_ANALYSIS
      : DisplayMode.ARTISTIC;
    
    this.setState({ displayMode: newMode });
  }

  /**
   * Toggle debug overlay on/off
   * Validates Requirements 3.4.7
   */
  toggleDebugOverlay(): void {
    this.setState({ debugOverlayEnabled: !this.state.debugOverlayEnabled });
  }

  /**
   * Set debug overlay state explicitly
   * Validates Requirements 3.4.7
   * @param enabled Whether debug overlay should be enabled
   */
  setDebugOverlay(enabled: boolean): void {
    this.setState({ debugOverlayEnabled: enabled });
  }

  /**
   * Handle gesture events and apply corresponding state changes
   * Validates Requirements 3.4.3, 3.4.4, 4.2, 4.5
   * @param gesture Gesture event to handle
   * @param personId Optional person identifier for multi-person mode
   */
  handleGesture(gesture: GestureEvent, personId?: string): void {
    // Log gesture action for debugging
    if (personId) {
      logger.log(`[StateManager] Handling gesture for person ${personId}: ${gesture.type} (confidence: ${gesture.confidence.toFixed(2)})`);
    } else {
      logger.log(`[StateManager] Handling gesture: ${gesture.type} (confidence: ${gesture.confidence.toFixed(2)})`);
    }
    
    // Apply gesture action mapping
    const stateUpdate = GestureActionMapper.applyGestureAction(gesture, this.state, personId);
    
    // Update state if there are changes
    if (Object.keys(stateUpdate).length > 0) {
      this.setState(stateUpdate);
    }
  }

  /**
   * Set a specific person's visualization type (manual selector action).
   * Applies an immutable per-person update and notifies subscribers.
   *
   * @param personId Person whose visualization type should change
   * @param type The visualization type to assign
   */
  setPersonVisualizationType(personId: PersonID, type: VisualizationType): void {
    const stateUpdate = GestureActionMapper.setVisualizationType(this.state, personId, type);
    if (Object.keys(stateUpdate).length > 0) {
      logger.log(`[StateManager] Set visualization for person ${personId}: ${type}`);
      this.setState(stateUpdate);
    }
  }

  /**
   * Get person collection
   * Validates Requirements 10.1, 10.2
   * @returns Current person collection
   */
  getPeople(): PersonCollection {
    return {
      people: new Map(this.state.people.people),
      frameCount: this.state.people.frameCount
    };
  }

  /**
   * Update person collection
   * Validates Requirements 10.1, 10.2
   * @param people New person collection
   */
  updatePeople(people: PersonCollection): void {
    this.setState({ people });
  }

  /**
   * Notify all subscribers of state change
   */
  private notifySubscribers(): void {
    const currentState = this.getState();
    this.subscribers.forEach(callback => callback(currentState));
  }
}
